

import { Inject, Injectable } from '@angular/core';
import { CrudService } from '@services/firestore-crud.service';
import { collection, CollectionReference, Firestore, getDocs } from 'firebase/firestore';
import { FirebaseStorage } from 'firebase/storage';
import { FIRESTORE_TOKEN, STORAGE_TOKEN } from './firebase.tokens';

export interface Person {
  age: number | null;
  biographyUrl: string | null;
  birthDay: number | null;
  birthDeath: string | null;
  birthMonth: number | null;
  birthYear: number | null;
  category: string | null;
  city: string | null;
  commonName: string | null;
  created: number | null;
  epoch: string | null;
  firstName: string | null;
  frontPage: boolean;
  fullName: string | null;
  fullTitle: string | null;
  id?: string | null;
  imagePath?: string | null;
  instagramUrl: string | null;
  instagramPosts: string | null;
  keyNotes: string | null;
  lastName: string | null;
  nickName: string | null;
  porekloUrl: string | null;
  shortDescription: string | null;
  slug: string | null;
  topTen: boolean;
  localHero: boolean;
  tags?: string[] | null;
  updated: number | null;
  wikiUrl: string | null;
  workPieces: string | null;
  workPiecesTitle: string | null;
}

export interface PersonGame extends Person {
  correctOrder?: boolean;
}

let localPersons: Person[] = [];

@Injectable({ providedIn: 'root' })
export class PersonsService extends CrudService<Person> {
  override collectionName = 'persons';

  constructor(
    @Inject(FIRESTORE_TOKEN) firestore: Firestore,
    @Inject(STORAGE_TOKEN) storage: FirebaseStorage
  ) {
    super(firestore, storage);
  }

  async getItemsServer(): Promise<Person[]> {
    const itemCollection: CollectionReference = collection(this.firestore, this.collectionName);
    const snap = await getDocs(itemCollection);

    const persons: Person[] = snap.docs.map((doc) => ({
      ...(doc.data() as Person),
      id: doc.id
    }));

    localPersons = [...persons];

    return persons;
  }

  override async getItems(): Promise<Person[]> {
    return localPersons;
  }


  override async getItemsQuery(
    queryField: string,
    queryOperator: any,
    queryValue: any
  ): Promise<Person[]> {
    const result = localPersons.filter((p: any) => {
      const value = p[queryField];

      if (queryOperator === 'in') {
        const queryArray = queryValue || [];
        return queryArray.includes(value);
      }

      if (queryOperator === '!=') {
        return queryValue !== value;
      }

      if (queryOperator === 'array-contains') {
        return Array.isArray(value) && value.includes(queryValue);
      }

      return queryValue === value;
    });

    return result;
  }

  override async getItemsQueries(conditions: [string, any][]): Promise<Person[]> {
    const result = localPersons.filter((p: any) => {
      const c1 = conditions[0];
      const c2 = conditions[1];
      const c3 = conditions[2];
      const conditionOne = c1 ? p[c1[0]] === c1[1] : true;
      const conditionTwo = c2 ? p[c2[0]] === c2[1] : true;
      const conditionThree = c3 ? p[c3[0]] === c3[1] : true;

      return conditionOne && conditionTwo && conditionThree;
    });

    return result;
  }

  override async getItemsSorted(sortColumn: string, sortOrder: string = 'asc'): Promise<Person[]> {
    const listCopy: Person[] = [...localPersons];
    const result = listCopy.filter(p => !!(p as any)[sortColumn]);
    const sort = sortOrder === 'asc' ? 1 : -1;

    result.sort((a: any, b: any) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      const isNumber = typeof aVal === 'number' && typeof bVal === 'number';
      if (isNumber) {
        return sort * (aVal - bVal);
      }

      const first = String(aVal);
      const second = String(bVal);
      return sort > 0
        ? first.localeCompare(second)
        : second.localeCompare(first);
    });

    return result;
  }

  override async getItem(id: string): Promise<Person> {
    const item = localPersons.find(p => p.id === id);
    if (!item) {
      throw new Error(`Person with id '${id}' not found`);
    }
    return item;
  }


  override async getItemBySlug(slug: string): Promise<Person | null> {
    return localPersons.find(p => p.slug === slug) || null;
  }

}
